import { Group, ScrollArea, SegmentedControl, Stack, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { FilterPanelGroup, FilterPanelToggle } from '../../../components/filters/filter-panel-group';
import { FilterPanelHeader } from '../../../components/filters/filter-panel-header';
import { FilterPanelToggleSlider } from '../../../components/filters/filter-panel-toggle-slider';
import { SmallSquareButton } from '../../../components/small-square-button/small-square-button';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  resetOverviewFilters,
  setOverviewCommittedNextWeeksValue,
  setOverviewModeBookmarked,
  setOverviewModeCommitted,
  setOverviewModeIncludeEmpty,
  setOverviewModeIsActiveOnly,
  setOverviewModeShowHidden,
  setOverviewViewMode,
  toggleCollapsedGroup,
} from '../../../store/strategy-store/strategy-store.actions';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import type { FilterGroup } from '../initiative-list/filters/filters.type';
import { useFilterGroups } from './overview-filters.hooks';

export function OverviewFilters({
  setShow,
  searchTerm,
  setSearchTerm,
}: {
  setShow: (show: boolean) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}) {
  const i18n = useTranslation();
  const filterGroups = useFilterGroups();
  const collapsedGroups = useStrategyStore((state) => state.collapsedGroups);
  const viewMode = useStrategyStore((state) => state.overviewFilters.view_mode);
  const modeBookmarked = useStrategyStore((state) => state.overviewFilters.mode_bookmarked);
  const modeCommitted = useStrategyStore((state) => state.overviewFilters.mode_committed);
  const modeIncludeEmpty = useStrategyStore((state) => state.overviewFilters.mode_include_empty);
  const modeIsActiveOnly = useStrategyStore((state) => state.overviewFilters.mode_is_active_only);
  const modeShowHidden = useStrategyStore((state) => state.overviewFilters.mode_show_hidden);
  const committedNextWeeksValue = useStrategyStore((state) => state.overviewFilters.committed_next_weeks_value);

  const filterLabel = viewMode === 'goals' ? i18n.t('common.strategic_goal') : i18n.t('common.outcome');

  const isGroupCollapsed = (groupId: string): boolean => {
    return collapsedGroups[groupId];
  };

  const isAllSelected = (group: FilterGroup): boolean => {
    return (
      (group.selectedValues || []).length > 0 && (group.selectedValues || []).length === (group.options || []).length
    );
  };

  const handleAllChange = (group: FilterGroup, isChecked: boolean): void => {
    group.onSelectionChange(isChecked ? group.options.map((opt) => opt.value) : []);
  };

  const handleIndividualChange = (group: FilterGroup, value: string, isChecked: boolean): void => {
    const newValues = isChecked ? [...group.selectedValues, value] : group.selectedValues.filter((v) => v !== value);

    group.onSelectionChange(newValues);
  };

  const handleResetFilters = () => {
    resetOverviewFilters();
    setSearchTerm('');
  };

  return (
    <Stack gap="md" h="100%">
      <Group justify="space-between" align="center">
        <Text size="medium" weight="medium">
          {`${viewMode === 'goals' ? i18n.t('common.strategic_goal') : i18n.t('common.outcome')} Filters`}
        </Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Text size="tiny" style={{ color: '#999', marginTop: -8 }}>
        {`Filter ${filterLabel}s to refine your view`}
      </Text>
      <Divider />
      <FilterPanelHeader onResetFilters={handleResetFilters} />
      <Divider />

      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack gap="md" pr="sm">
          <TextInput
            placeholder={`Search ${filterLabel}s...`}
            radius="xl"
            size="sm"
            value={searchTerm}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
          />
          <Divider />

          {/* View Mode Toggle */}
          <Stack gap={8}>
            <Text size="small" weight="medium">
              View Mode
            </Text>
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setOverviewViewMode(value as 'goals' | 'outcomes')}
              data={[
                { label: i18n.t('common.strategic_goals'), value: 'goals' },
                { label: i18n.t('common.outcomes'), value: 'outcomes' },
              ]}
              fullWidth
            />
          </Stack>
          <Divider />

          {/* Include Empty Filter */}
          <FilterPanelToggle
            toggle={{
              id: 'mode_include_empty',
              name: i18n.t('strategy.filters.include_empty'),
              icon: 'folder_open',
              selectedValue: modeIncludeEmpty,
              onSelectionChange: setOverviewModeIncludeEmpty,
            }}
            hasDivider
          />

          {/* Bookmarked Only Filter */}
          <FilterPanelToggle
            toggle={{
              id: 'mode_bookmarked',
              name: i18n.t('strategy.filters.bookmarked_only'),
              icon: 'bookmark_added',
              selectedValue: modeBookmarked,
              onSelectionChange: setOverviewModeBookmarked,
            }}
            hasDivider
          />

          {/* Active Only Filter */}
          <FilterPanelToggle
            toggle={{
              id: 'mode_is_active_only',
              name: 'Active Only',
              icon: 'toggle_on',
              selectedValue: modeIsActiveOnly,
              onSelectionChange: setOverviewModeIsActiveOnly,
            }}
            hasDivider
          />

          {/* Show Hidden Filter — Outcomes only; Strategic Goals view has no hide infrastructure */}
          {viewMode === 'outcomes' && (
            <FilterPanelToggle
              toggle={{
                id: 'mode_show_hidden',
                name: i18n.t('strategy.filters.show_hidden'),
                icon: 'visibility_off',
                selectedValue: modeShowHidden,
                onSelectionChange: setOverviewModeShowHidden,
              }}
              hasDivider
            />
          )}

          {/* Committed Next Weeks Filter */}
          <FilterPanelToggleSlider
            toggle={{
              id: 'mode_committed',
              icon: 'calendar_month',
              name: i18n.t('strategy.filters.committed_next_weeks'),
              tooltip: `Filter ${filterLabel}s by their committed date. Shows ${filterLabel}s with end dates in the next N weeks.`,
              toggleValue: modeCommitted,
              onToggleChange: setOverviewModeCommitted,
              sliderValue: committedNextWeeksValue,
              onSliderChange: setOverviewCommittedNextWeeksValue,
              sliderMin: 1,
              sliderMax: 16,
              sliderStep: 1,
              sliderLabel: (value) => `Next ${value} ${value === 1 ? 'week' : 'weeks'}`,
            }}
            hasDivider={filterGroups.length > 0}
          />

          {/* Objective Filter Groups */}
          {filterGroups.map((group) => (
            <FilterPanelGroup
              key={group.id}
              group={group}
              toggleCollapsed={(group) => toggleCollapsedGroup(group.id)}
              isCollapsed={isGroupCollapsed(group.id)}
              isAllSelected={isAllSelected(group)}
              handleAllChange={handleAllChange}
              handleIndividualChange={handleIndividualChange}
              hasDivider={group !== filterGroups[filterGroups.length - 1]}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
